import { useEffect, useRef } from 'react';
import { QUOTE_MAX_GUESTS } from '@casa-aragon/utils';
import { identify, track } from './tracking';
import { getQuoteFlowAssignment } from '@/lib/quoteFlowAssignment';
import type {
  GuestCountBucket,
  LeadSubmitErrorType,
  MobileInputPrefix,
  QuoteSource,
  QuoteStepId,
  TrackingEventMap,
  ValidationErrorType,
  ValidationField,
} from './events';

// One quote-wizard visit, from open/resume until submit or close. Lives at module
// level (not in EventWizard state) so it survives React StrictMode's double effects
// and so the pages hosting the wizard dialog can report the close.

interface QuoteSession {
  source: QuoteSource;
  startedAt: number;
  stepId: QuoteStepId;
  stepIndex: number;
  stepViewedAt: number;
  viewedIndex: number | null;
  completed: boolean;
}

let session: QuoteSession | null = null;
let pageListenersAdded = false;
// The open visit at pagehide. If the browser restores the page from its back/forward cache,
// the wizard is still on screen but never remounts, so the visit is resumed from here.
let suspended: Pick<QuoteSession, 'source' | 'stepId' | 'stepIndex'> | null = null;

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const handlePageExit = () => {
  suspended =
    session && !session.completed
      ? { source: session.source, stepId: session.stepId, stepIndex: session.stepIndex }
      : null;
  closeQuoteSession('page_exit');
};

const handlePageShow = (event: PageTransitionEvent) => {
  const resume = suspended;
  suspended = null;
  if (!event.persisted || !resume) return;
  startQuoteSession({ ...resume, restored: true, daysSinceSaved: 0 });
  // The visible step was already reported in this visit
  if (session) session.viewedIndex = resume.stepIndex;
};

export function startQuoteSession(args: {
  source: QuoteSource;
  stepId: QuoteStepId;
  stepIndex: number;
  restored: boolean;
  daysSinceSaved?: number | null;
}): void {
  // Already running: a StrictMode re-run or a remount of the same visit
  if (session) return;
  const t = now();
  session = {
    source: args.source,
    startedAt: t,
    stepId: args.stepId,
    stepIndex: args.stepIndex,
    stepViewedAt: t,
    viewedIndex: null,
    completed: false,
  };

  if (args.restored) {
    track('Quote Wizard Resumed', {
      source: args.source,
      resumed_step_id: args.stepId,
      resumed_step_index: args.stepIndex,
      days_since_saved: args.daysSinceSaved ?? null,
    });
  } else {
    track('Quote Wizard Opened', {
      source: args.source,
      restored: false,
      quote_flow: getQuoteFlowAssignment()?.flow,
    });
  }

  if (!pageListenersAdded && typeof window !== 'undefined') {
    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('pageshow', handlePageShow);
    pageListenersAdded = true;
  }
}

export function trackQuoteStepViewed(args: {
  stepId: QuoteStepId;
  stepIndex: number;
  totalSteps: number;
  restored: boolean;
}): void {
  if (!session || session.viewedIndex === args.stepIndex) return;
  session.viewedIndex = args.stepIndex;
  session.stepId = args.stepId;
  session.stepIndex = args.stepIndex;
  session.stepViewedAt = now();
  track('Quote Step Viewed', {
    step_id: args.stepId,
    step_index: args.stepIndex,
    step_number: args.stepIndex + 1,
    total_steps: args.totalSteps,
    restored: args.restored,
  });
}

export function trackQuoteStepCompleted(
  extra: Omit<TrackingEventMap['Quote Step Completed'], 'step_id' | 'step_index' | 'time_on_step_ms'>,
): void {
  if (!session) return;
  track('Quote Step Completed', {
    step_id: session.stepId,
    step_index: session.stepIndex,
    time_on_step_ms: Math.round(now() - session.stepViewedAt),
    ...extra,
  });
}

export function trackQuoteStepBack(toStepId: QuoteStepId): void {
  if (!session) return;
  track('Quote Step Back', { from_step_id: session.stepId, to_step_id: toStepId });
}

export const guestCountBucket = (count: number): GuestCountBucket => {
  if (count <= 30) return '4-30';
  if (count <= 50) return '31-50';
  if (count <= 80) return '51-80';
  if (count <= QUOTE_MAX_GUESTS) return '81-100';
  return '100+';
};

export function trackLeadSubmitted(args: {
  guestCount: number;
  planType: string;
  referralSource: string | null;
  stepsTotal: number;
  leadId?: string;
}): void {
  if (!session) return;
  session.completed = true;
  track('Lead Submitted', {
    source: session.source,
    guest_count: args.guestCount,
    guest_count_bucket: guestCountBucket(args.guestCount),
    plan_type: args.planType,
    referral_source: args.referralSource,
    time_to_complete_ms: Math.round(now() - session.startedAt),
    steps_total: args.stepsTotal,
    over_capacity: args.guestCount > QUOTE_MAX_GUESTS,
  });
  // The lead's database id is not PII; it ties the anonymous funnel to the lead
  if (args.leadId) identify(args.leadId);
}

type SubmitError = { code?: string; response?: { status?: number } } | null | undefined;

// Duplicate emails don't fail (the backend updates the open lead), so there is no 'duplicate' type
export const leadSubmitErrorType = (error: SubmitError): LeadSubmitErrorType => {
  const status = error?.response?.status;
  if (status === undefined) {
    return error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' ? 'timeout' : 'network';
  }
  if (status === 400 || status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'other';
};

/** Only the status and a category; the error message can echo what was typed */
export function trackLeadSubmitFailed(error: unknown): void {
  const submitError = error as SubmitError;
  track('Lead Submit Failed', {
    error_status: submitError?.response?.status ?? null,
    error_type: leadSubmitErrorType(submitError),
  });
}

/**
 * Ends the visit. Fires 'Quote Wizard Abandoned' unless the lead was submitted.
 * Safe to call when no visit is running.
 */
export function closeQuoteSession(reason: 'closed' | 'page_exit' = 'closed'): void {
  if (!session) return;
  const ended = session;
  session = null;
  if (ended.completed) return;
  track(
    'Quote Wizard Abandoned',
    {
      source: ended.source,
      last_step_id: ended.stepId,
      last_step_index: ended.stepIndex,
      time_in_wizard_ms: Math.round(now() - ended.startedAt),
      reason,
    },
    { beacon: reason === 'page_exit' },
  );
}

/**
 * Fires once each time a validation error becomes visible, never with the typed value. `describeInput` adds the
 * input's shape (read when the error fires).
 */
export function useTrackValidationError(
  enabled: boolean,
  stepId: QuoteStepId,
  field: ValidationField,
  errorType: ValidationErrorType | null,
  describeInput?: () => { input_digits: number; input_prefix: MobileInputPrefix },
): void {
  const last = useRef<ValidationErrorType | null>(null);
  const describeRef = useRef(describeInput);
  describeRef.current = describeInput;
  useEffect(() => {
    if (enabled && errorType && errorType !== last.current) {
      track('Quote Step Validation Error', {
        step_id: stepId,
        field,
        error_type: errorType,
        ...describeRef.current?.(),
      });
    }
    last.current = errorType;
  }, [enabled, stepId, field, errorType]);
}

/** Test-only: forget the running visit. */
export function __resetQuoteSessionForTests(): void {
  session = null;
  suspended = null;
}
